import React, { useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import GoogleMap from "./GoogleMap";
import SupportFilters from "./SupportFilters";
import { SearchContext } from "./SearchContext";
import { BeatLoader } from "react-spinners";
import ResultContainer from "./ResultContainer";
import { GOOGLE_MAPS_API_KEY } from "./GoogleMapKey";
import { useJsApiLoader } from "@react-google-maps/api";
import Drawer from "@mui/material/Drawer";
import { FaRectangleXmark } from "react-icons/fa6";
import { sortProviders } from "../utils/sorting";

const LIBRARIES = ["places"];

const SearchResults = () => {
  const { originalData, loading } = useContext(SearchContext);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Sorting params
  const sortOrder = searchParams.get("sort") || "asc";
  const orderBy = searchParams.get("orderby") || "last_updated";

  // Lat/lng for the map
  const lat = parseFloat(searchParams.get("lat")) || 45.0;
  const lng = parseFloat(searchParams.get("lng")) || -122.0;
  const radius = parseInt(searchParams.get("radius") || "25", 10);

  // Support param
  const supportParam = searchParams.get("support_term");
  const selectedSupports = !supportParam || supportParam === "all" ? [] : supportParam.split(",");

  // Advanced search param
  const advSearchParam = searchParams.get("adv_search_term");
  const selectedAdvSearch = !advSearchParam || advSearchParam === "all" ? [] : advSearchParam.split(",");

  // Combine filters for final data
  const filteredData = useMemo(() => {
    // Start with original data
    let data = [...originalData];

    // ----- 1) Apply Support filters -----
    if (selectedSupports.includes("none")) {
      data = data.filter(item => item.support_terms.length === 0);
    } else if (selectedSupports.length > 0) {
      data = data.filter(item => {
        const termIds = item.support_terms.map(t => t.term_id.toString());
        // Keep items that match at least one of the selectedSupports
        return selectedSupports.some(supportId => termIds.includes(supportId));
      });
    }

    // ----- 2) Apply Advanced Search filters -----
    if (selectedAdvSearch.length > 0) {
      data = data.filter(item => {
        // Pull all advanced term IDs from the provider
        const advTermIds = item.advanced_services.map(t => t.term_id.toString());
        // Keep items that match at least one of the advancedSearch IDs
        return selectedAdvSearch.some(advId => advTermIds.includes(advId));
      });
    }

    return data;
  }, [originalData, selectedSupports, selectedAdvSearch]);

  // Sort the final filtered data
  const sortedData = useMemo(() => {
    return sortProviders(filteredData, orderBy, sortOrder);
  }, [filteredData, orderBy, sortOrder]);

  // For map hover highlighting
  const [hoveredProviderId, setHoveredProviderId] = useState(null);

  // Load Google Maps script
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  // Mobile toggles
  const [showFilters, setShowFilters] = useState(false);
  const [showMap, setShowMap] = useState(false);

  return (
    <>
      <section className="flex">
        {/* Desktop filters aside */}
        <aside id="filters" className="hidden md:block w-[275px] shrink-0 p-2 bg-slate-50">
          <SupportFilters />
        </aside>

        <main className="flex-grow p-2 border-primaryBlue md:border-l">
          {loading && (
            <div className="flex justify-center items-center flex-col">
              <BeatLoader color={"#009999"} loading={loading} size={25} />
              <div>Loading Search Results....</div>
            </div>
          )}

          {!loading && <ResultContainer data={sortedData} hoveredProviderId={hoveredProviderId} setHoveredProviderId={setHoveredProviderId} setShowFilters={setShowFilters} setShowMap={setShowMap} radius={radius} />}
        </main>

        {/* Desktop map aside */}
        <aside id="google-map" className="hidden md:block w-[600px] shrink-0 p-2 max-h-[800px]">
          {!isLoaded ? (
            <div>Loading Map...</div>
          ) : (
            <GoogleMap
              center={{ lat, lng }}
              radius={radius}
              providers={sortedData}
              hoveredProviderId={hoveredProviderId}
              setHoveredProviderId={setHoveredProviderId}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: true,
                scaleControl: true
              }}
            />
          )}
        </aside>
      </section>

      {/* Mobile Drawer: Filters */}
      <Drawer anchor="left" open={showFilters} onClose={() => setShowFilters(false)}>
        <div className="w-[225px] bg-slate-50 h-full p-2 relative">
          <button className="p-2 text-red-500 text-2xl absolute top-0 right-0" onClick={() => setShowFilters(false)}>
            <FaRectangleXmark />
          </button>
          <SupportFilters />
        </div>
      </Drawer>

      {/* Mobile Drawer: Map */}
      <Drawer
        anchor="right"
        open={showMap}
        onClose={() => setShowMap(false)}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100%"
          }
        }}
      >
        <div className="w-full bg-white h-full overflow-auto p-2 relative">
          <button className="p-2 text-red-500 text-2xl absolute top-0 left-0 z-20" onClick={() => setShowMap(false)}>
            <FaRectangleXmark />
          </button>

          {!isLoaded ? (
            <div>Loading Map...</div>
          ) : (
            <GoogleMap
              center={{ lat, lng }}
              radius={radius}
              providers={sortedData}
              hoveredProviderId={hoveredProviderId}
              setHoveredProviderId={setHoveredProviderId}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: true,
                scaleControl: true
              }}
            />
          )}
        </div>
      </Drawer>
    </>
  );
};

export default SearchResults;
